// LT-359 (LT-326) — Typed client for /contact-lists/*. Mirrors the
// shape conventions of contactsAPI.ts.

import axios from 'axios';
import {
  ContactList,
  ContactListCreate,
  ContactListEvaluateResponse,
  ContactListMembersUpdate,
  ContactListMembersUpdateResponse,
  ContactListSnapshot,
  ContactListSnapshotCreate,
  ContactListUpdate,
} from '../types/contactList';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ListContactListsParams {
  limit?: number;
  offset?: number;
}

export const contactListsAPI = {
  list: async (params?: ListContactListsParams): Promise<ContactList[]> => {
    const r = await client.get<ContactList[]>('/contact-lists/', { params });
    return r.data;
  },

  get: async (id: string): Promise<ContactList> => {
    const r = await client.get<ContactList>(`/contact-lists/${id}`);
    return r.data;
  },

  create: async (payload: ContactListCreate): Promise<ContactList> => {
    const r = await client.post<ContactList>('/contact-lists/', payload);
    return r.data;
  },

  update: async (id: string, patch: ContactListUpdate): Promise<ContactList> => {
    const r = await client.patch<ContactList>(`/contact-lists/${id}`, patch);
    return r.data;
  },

  remove: async (id: string): Promise<void> => {
    await client.delete(`/contact-lists/${id}`);
  },

  evaluate: async (id: string): Promise<ContactListEvaluateResponse> => {
    const r = await client.get<ContactListEvaluateResponse>(`/contact-lists/${id}/members`);
    return r.data;
  },

  addMembers: async (
    id: string,
    payload: ContactListMembersUpdate,
  ): Promise<ContactListMembersUpdateResponse> => {
    const r = await client.post<ContactListMembersUpdateResponse>(
      `/contact-lists/${id}/members`,
      payload,
    );
    return r.data;
  },

  removeMembers: async (
    id: string,
    payload: ContactListMembersUpdate,
  ): Promise<ContactListMembersUpdateResponse> => {
    const r = await client.delete<ContactListMembersUpdateResponse>(
      `/contact-lists/${id}/members`,
      { data: payload },
    );
    return r.data;
  },

  snapshot: async (
    id: string,
    payload: ContactListSnapshotCreate = {},
  ): Promise<ContactListSnapshot> => {
    const r = await client.post<ContactListSnapshot>(
      `/contact-lists/${id}/snapshot`,
      payload,
    );
    return r.data;
  },
};
